import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Progress } from 'antd';
import styled from 'styled-components';

import { Colors } from 'context';
import { TrackIcon } from 'atoms/TrackIcon/TrackIcon';
import { careerType } from 'services/careerType';
import { mobile } from 'services/ui';
import { ISkillProfession } from 'core/hooks/Growth/transform/transformSkillDetail';
import { professionHref } from 'router/careerLinks';

/**
 * Профессия, которой нужен этот навык (ТЗ 0070, раздел 4.7.10).
 *
 * Композиция повторяет каталог профессий, где правило записано словами:
 * **кольцо говорит о прогрессе, знак — о том, что это за профессия**. Поэтому
 * знак стоит перед названием, а не вместо кольца, и роль читается одинаково
 * на всех трёх экранах.
 *
 * 🔴 Кольцо помечено `aria-hidden`, а дробь остаётся словами: точное «Закрыто
 * 5 из 12» уже произносится строкой ниже, и второй `progressbar` внутри
 * ссылки только удлинял бы объявление, ничего не добавляя.
 */
interface ISkillProfessionRow {
  profession: ISkillProfession;
}

export const SkillProfessionRow = memo(({ profession }: ISkillProfessionRow) => {
  const { t } = useTranslation();
  const percent = profession.totalCount
    ? Math.round((profession.haveCount / profession.totalCount) * 100)
    : 0;

  return (
    <Row>
      <TrackIcon icon={profession.icon} size={28} />
      <Body>
        {/* Клик работает по всей плитке, а доступное имя ссылки остаётся
            коротким — тот же приём, что у задач недели. */}
        <TitleLink to={professionHref(profession.code)}>{profession.title}</TitleLink>
        <Meta>
          {t('careerCenter.map.professionProgress', {
            have: profession.haveCount,
            total: profession.totalCount,
          })}
        </Meta>
      </Body>
      <RingBox aria-hidden>
        <Progress
          type="circle"
          size={52}
          percent={percent}
          strokeColor={Colors.signal_green}
          trailColor={Colors.system_gray}
          format={() => `${profession.haveCount}/${profession.totalCount}`}
        />
      </RingBox>
    </Row>
  );
});

const Row = styled.li`
  position: relative;
  display: grid;
  /* minmax(0, 1fr) — иначе длинное название профессии распирает сетку и
     выталкивает кольцо за край. */
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 12px;
  list-style: none;

  &:hover {
    border-color: ${Colors.gray_dark};
  }

  &:focus-within {
    outline: 2px solid ${Colors.black};
    outline-offset: 2px;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const TitleLink = styled(Link)`
  ${careerType.bodyStrong()};
  color: ${Colors.black};
  text-decoration: none;
  overflow-wrap: anywhere;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
  }
`;

const Meta = styled.span`
  ${careerType.meta()};
  color: ${Colors.gray_dark};
`;

const RingBox = styled.div`
  flex: none;

  /* Шрифт внутри кольца antd считает от его размера и уходит ниже пола
     раздела (14 px) — возвращаем. */
  .ant-progress-text {
    ${careerType.meta()};
    color: ${Colors.black};
  }

  ${mobile} {
    .ant-progress {
      width: 44px !important;
      height: 44px !important;
    }
  }
`;
